/**
 * Project Stats Bar Component
 * Displays aggregate statistics for projects
 */

import { FolderKanban, DollarSign, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ProjectsStats } from '../types';

interface ProjectStatsBarProps {
  stats?: ProjectsStats;
  isLoading?: boolean;
}

export function ProjectStatsBar({ stats, isLoading }: ProjectStatsBarProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-4">
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-6 w-16" />
          </Card>
        ))}
      </div>
    );
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'None scheduled';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      <Card className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <FolderKanban className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Active Projects</p>
          <p className="text-2xl font-semibold">{stats?.activeProjects ?? 0}</p>
        </div>
      </Card>

      <Card className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
          <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Invoice Amount</p>
          <p className="text-2xl font-semibold">
            {formatCurrency(stats?.totalInvoiceAmount ?? 0, stats?.currency ?? 'USD')}
          </p>
        </div>
      </Card>

      <Card className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
          <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Next Installation</p>
          <p className="text-2xl font-semibold">{formatDate(stats?.nextInstallationDate)}</p>
        </div>
      </Card>
    </div>
  );
}
